import { CredentialType } from "./enums";
import { CREDENTIAL_EXPIRY_WARNING_DAYS } from "@/services/credentials/credential-status-spec";
import {
  credentialStatusVocabulary,
  LICENSE_TYPE_LABELS,
  LicenseType,
  LifeSupportKind,
} from "./credential-vocabularies";

/**
 * How to render an edit form for each credential type.
 *
 * The credential-type registry defines what a credential *is* — its identity
 * rule and the Zod contract for `details` — but says nothing about how to ask a
 * human for it: no labels, no field order, no input kinds, no enum options. A
 * form cannot be generated from `detailsSchema` alone, and hand-writing 28
 * forms guarantees they drift from the schemas that validate them.
 *
 * **This lives on the API side deliberately.** `api/tsconfig.json` sets
 * `rootDir: ./src`, so the API cannot import a file under `web_client/` — a
 * parity test in `api/src` checking descriptors that lived in the client would
 * have to duplicate the keys it was checking, and a test that duplicates its
 * subject detects nothing. The dependency runs one way: the client maps
 * `@api/* → ../api/src/*` and already imports `@api/shared-runtime/enums`
 * throughout. `credential-form-descriptor-parity.test.ts` is what keeps these
 * honest against the registry.
 */

export type CredentialFormFieldKind =
  | "text"
  | "textarea"
  | "date"
  | "number"
  | "boolean"
  | "select"
  /** US state / territory code — the client renders its own picker. */
  | "state"
  /** Free-form list of short strings (DEA schedules, taxonomy codes). */
  | "string-list";

export type CredentialFormField = {
  /**
   * Where the value belongs in the write payload: a promoted column on
   * `credential_version`, or a key inside `details`.
   */
  source: "column" | "details";
  key: string;
  label: string;
  kind: CredentialFormFieldKind;
  required?: boolean;
  options?: readonly { value: string; label: string }[];
  helpText?: string;
  /**
   * This field feeds `computeIdentityKey`, so changing it describes a
   * *different* real-world credential. The write path rejects that
   * ([write-credential.ts] `updateCredential`); the form should render these
   * read-only when editing so the user meets the rule as a locked field rather
   * than as an error after typing.
   */
  isIdentity?: boolean;
};

export type CredentialFormDescriptor = {
  type: CredentialType;
  fields: readonly CredentialFormField[];
  /**
   * `details` keys this form deliberately cannot edit yet — nested objects,
   * repeating rows, and free-form records that need their own sub-forms.
   *
   * Listed rather than omitted so the parity test can assert *total* coverage:
   * every schema key is either editable or explicitly deferred. Without this a
   * forgotten field and a deferred one look identical.
   *
   * The form tells the reader these exist, because they are values of theirs it
   * is not showing — so keep out anything they never entered.
   */
  unsupportedDetailKeys: readonly string[];
  /**
   * `details` keys the SERVER writes and owns — provenance the form neither
   * asks for nor withholds.
   *
   * Counted as covered by the parity test, but never announced: telling someone
   * "some fields cannot be edited here" about a key they have never seen and
   * would not recognise turns a complete form into one that looks broken.
   */
  serverOwnedDetailKeys?: readonly string[];
};

/**
 * Shared by every expirable type, so the promise is made once and identically.
 *
 * The date is what turns a stored document into something the system watches:
 * without it a credential is simply "on file" forever, and nobody learns it
 * lapsed. Saying so at the field is the only place a reader is deciding whether
 * to bother typing it.
 *
 * The window is read from the status spec rather than written out, so the
 * sentence cannot drift from the rule the dashboard actually applies.
 */
const EXPIRES_AT: CredentialFormField = {
  source: "column",
  key: "expiresAt",
  label: "Expiration date",
  kind: "date",
  helpText: `With a date here, this credential is flagged ${CREDENTIAL_EXPIRY_WARNING_DAYS} days before it expires and included in the weekly summary email. Leave it blank if it does not expire.`,
};
const ISSUED_AT: CredentialFormField = {
  source: "column",
  key: "issuedAt",
  label: "Issue date",
  kind: "date",
};
/**
 * What the document says about its holder, for comparing against the person's
 * own record rather than replacing it.
 *
 * The labels say "on document" out loud: a certificate in a maiden name is
 * ordinary, and a reviewer needs to see that difference rather than have the
 * form imply the two must agree. Personal Info remains where the person's name
 * and date of birth live.
 */
const NAME_ON_DOCUMENT: CredentialFormField = {
  source: "details",
  key: "nameOnDocument",
  label: "Name on document",
  kind: "text",
  helpText: "Exactly as printed, even where it differs from their legal name.",
};
const BIRTH_DATE_ON_DOCUMENT: CredentialFormField = {
  source: "details",
  key: "birthDateOnDocument",
  label: "Date of birth on document",
  kind: "date",
};

/**
 * The promoted `status` column, as a select over that type's vocabulary.
 *
 * Always a select, never free text: `status` is queryable, and ad-hoc spellings
 * in it cannot be reconciled with the values already stored. A type with no
 * documented vocabulary gets no status field at all rather than an open one.
 */
function statusField(
  type: CredentialType,
  label = "Status",
): CredentialFormField {
  const vocabulary = credentialStatusVocabulary(type);
  if (!vocabulary) {
    throw new Error(`No status vocabulary for ${type}`);
  }
  return {
    source: "column",
    key: "status",
    label,
    kind: "select",
    options: vocabulary.map((value) => ({ value, label: value })),
  };
}

/** The state a credential was issued in. Identity for the types keyed on it. */
const stateField = (
  label: string,
  isIdentity = false,
): CredentialFormField => ({
  source: "column",
  key: "stateCode",
  label,
  kind: "state",
  isIdentity,
  required: isIdentity,
});

const numberField = (label: string): CredentialFormField => ({
  source: "column",
  key: "number",
  label,
  kind: "text",
});

const text = (key: string, label: string): CredentialFormField => ({
  source: "details",
  key,
  label,
  kind: "text",
});
const bool = (key: string, label: string): CredentialFormField => ({
  source: "details",
  key,
  label,
  kind: "boolean",
});
const date = (key: string, label: string): CredentialFormField => ({
  source: "details",
  key,
  label,
  kind: "date",
});
const num = (key: string, label: string): CredentialFormField => ({
  source: "details",
  key,
  label,
  kind: "number",
});
const list = (key: string, label: string): CredentialFormField => ({
  source: "details",
  key,
  label,
  kind: "string-list",
});
const select = ({
  key,
  label,
  values,
  ...extra
}: {
  key: string;
  label: string;
  values: readonly string[];
} & Partial<CredentialFormField>): CredentialFormField => ({
  source: "details",
  key,
  label,
  kind: "select",
  options: values.map((value) => ({ value, label: value })),
  ...extra,
});

export const CREDENTIAL_FORM_DESCRIPTORS: Readonly<
  Record<CredentialType, CredentialFormDescriptor>
> = {
  [CredentialType.StateLicense]: {
    type: CredentialType.StateLicense,
    fields: [
      stateField("State", true),
      numberField("License number"),
      ISSUED_AT,
      EXPIRES_AT,
      statusField(CredentialType.StateLicense, "License status"),
      {
        source: "details",
        key: "licenseType",
        label: "License type",
        kind: "select",
        // Codes are what we store; the discipline name is what a person reads,
        // so the picker is ordered by name — code order reads as scrambled.
        options: Object.values(LicenseType)
          .map((value) => ({ value, label: LICENSE_TYPE_LABELS[value] }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        helpText: "The discipline this licence was issued in.",
      },
      bool(
        "isCurrentlyPracticingInState",
        "Currently practising in this state",
      ),
      bool("compact", "IMLC compact letter of qualification"),
      list("imlcStateCodes", "Compact states granted"),
    ],
    unsupportedDetailKeys: [],
  },
  [CredentialType.Dea]: {
    type: CredentialType.Dea,
    fields: [
      {
        ...stateField("State", false),
        required: false,
        helpText: "Optional when the DEA registration is not state-specific.",
      },
      { ...numberField("DEA number"), isIdentity: true },
      ISSUED_AT,
      EXPIRES_AT,
      statusField(CredentialType.Dea, "Registration status"),
      list("schedules", "Schedules"),
      bool("licenseUnlimited", "Unlimited"),
      text("businessActivity", "Business activity"),
      text("registeredAddress", "Registered address"),
    ],
    unsupportedDetailKeys: [],
  },
  [CredentialType.Cds]: {
    type: CredentialType.Cds,
    fields: [
      stateField("State", true),
      numberField("CDS number"),
      ISSUED_AT,
      EXPIRES_AT,
      statusField(CredentialType.Cds, "Registration status"),
      bool(
        "isCurrentlyPracticingInState",
        "Currently practising in this state",
      ),
      list("schedules", "Schedules"),
      { ...text("notes", "Notes"), kind: "textarea" },
    ],
    unsupportedDetailKeys: [],
  },
  [CredentialType.BoardCertification]: {
    type: CredentialType.BoardCertification,
    fields: [
      {
        ...text("certifyingBoardName", "Certifying board"),
        required: true,
        isIdentity: true,
      },
      { ...text("specialty", "Specialty"), isIdentity: true },
      numberField("Certificate number"),
      ISSUED_AT,
      EXPIRES_AT,
      statusField(CredentialType.BoardCertification, "Certification status"),
      text("specialtyNuccCode", "NUCC taxonomy code"),
      text("specialtyLevel", "Specialty level"),
      date("recertificationDate", "Recertification date"),
      date("boardExamDate", "Board exam date"),
      {
        ...text("notBoardCertifiedReason", "Reason not board certified"),
        kind: "textarea",
      },
      text("otherPracticeInterestOrFocus", "Other practice interest or focus"),
    ],
    unsupportedDetailKeys: ["directoryListings"],
  },
  [CredentialType.Ecfmg]: {
    type: CredentialType.Ecfmg,
    fields: [numberField("Certificate number"), ISSUED_AT],
    unsupportedDetailKeys: [],
  },
  [CredentialType.Education]: {
    type: CredentialType.Education,
    fields: [
      select({
        key: "kind",
        label: "Kind",
        values: ["degree", "fifth-pathway", "usmle"],
        isIdentity: true,
      }),
      { ...text("institutionName", "Institution"), isIdentity: true },
      { ...text("degree", "Degree"), isIdentity: true },
      text("program", "Program"),
      text("specialty", "Specialty"),
      date("startDate", "Start date"),
      date("endDate", "End date"),
      text("city", "City"),
      text("country", "Country"),
      bool("isPrimary", "Primary education"),
    ],
    unsupportedDetailKeys: ["address", "usmleSteps"],
  },
  [CredentialType.Training]: {
    type: CredentialType.Training,
    fields: [
      { ...text("institutionName", "Institution"), isIdentity: true },
      { ...text("trainingType", "Training type"), isIdentity: true },
      { ...text("specialty", "Specialty"), isIdentity: true },
      text("degree", "Degree"),
      text("program", "Program"),
      text("affiliatedUniversity", "Affiliated university"),
      date("startDate", "Start date"),
      date("endDate", "End date"),
      bool("isProgramSuccessfullyCompleted", "Successfully completed"),
      text("programDirector", "Program director"),
      text("currentProgramDirector", "Current program director"),
      text("city", "City"),
      text("country", "Country"),
    ],
    unsupportedDetailKeys: ["address"],
  },
  [CredentialType.LifeSupport]: {
    type: CredentialType.LifeSupport,
    fields: [
      {
        source: "details",
        key: "certKind",
        label: "Certification",
        kind: "select",
        options: Object.values(LifeSupportKind).map((value) => ({
          value,
          label:
            value === LifeSupportKind.Other ? "Other" : value.toUpperCase(),
        })),
        required: true,
        isIdentity: true,
      },
      ISSUED_AT,
      EXPIRES_AT,
      statusField(CredentialType.LifeSupport, "Card status"),
      text("issuingBody", "Issuing body"),
      // The promoted `number` column IS the eCard/cert number (type-schemas
      // §8), so offering both it and this asked for a "certificate number"
      // that an AHA eCard does not have. The eCard code is the identifier
      // printed on the card and the one the OCR pipeline extracts.
      text("eCardCode", "eCard code"),
    ],
    unsupportedDetailKeys: [],
  },
  [CredentialType.HealthScreening]: {
    type: CredentialType.HealthScreening,
    fields: [
      {
        ...text("screeningKind", "Screening"),
        required: true,
        isIdentity: true,
      },
      ISSUED_AT,
      EXPIRES_AT,
      statusField(CredentialType.HealthScreening, "Result status"),
      text("result", "Result"),
      text("method", "Method"),
      text("facility", "Facility"),
      bool("declination", "Declined"),
    ],
    unsupportedDetailKeys: [],
  },
  [CredentialType.BirthCertificate]: {
    type: CredentialType.BirthCertificate,
    fields: [
      NAME_ON_DOCUMENT,
      BIRTH_DATE_ON_DOCUMENT,
      text("birthPlace", "Place of birth"),
      numberField("Certificate number"),
      {
        ...text("issuingJurisdiction", "Issued by"),
        helpText: "The registrar, county or state named on the certificate.",
      },
      ISSUED_AT,
    ],
    unsupportedDetailKeys: [],
  },
  [CredentialType.Passport]: {
    type: CredentialType.Passport,
    fields: [
      numberField("Passport number"),
      ISSUED_AT,
      EXPIRES_AT,
      statusField(CredentialType.Passport),
      { ...text("issuingCountry", "Issuing country"), isIdentity: true },
      text("nationality", "Nationality"),
      NAME_ON_DOCUMENT,
      BIRTH_DATE_ON_DOCUMENT,
    ],
    unsupportedDetailKeys: [],
  },
  [CredentialType.DriversLicense]: {
    type: CredentialType.DriversLicense,
    fields: [
      stateField("State", true),
      numberField("Licence number"),
      ISSUED_AT,
      EXPIRES_AT,
      statusField(CredentialType.DriversLicense),
      NAME_ON_DOCUMENT,
      BIRTH_DATE_ON_DOCUMENT,
    ],
    unsupportedDetailKeys: [],
  },
  [CredentialType.GovernmentId]: {
    type: CredentialType.GovernmentId,
    fields: [
      { ...text("idKind", "ID type"), required: true, isIdentity: true },
      numberField("ID number"),
      ISSUED_AT,
      EXPIRES_AT,
      text("issuer", "Issuer"),
      NAME_ON_DOCUMENT,
      BIRTH_DATE_ON_DOCUMENT,
    ],
    unsupportedDetailKeys: [],
  },
  [CredentialType.MalpracticeInsurance]: {
    type: CredentialType.MalpracticeInsurance,
    fields: [
      { ...text("carrierName", "Carrier"), required: true },
      // The registry keys a policy on its number, not its carrier: changing
      // the number describes another policy, so it is the field to lock.
      { ...numberField("Policy number"), isIdentity: true },
      ISSUED_AT,
      EXPIRES_AT,
      statusField(CredentialType.MalpracticeInsurance, "Policy status"),
      num("perOccurrenceLimit", "Per-occurrence limit"),
      num("aggregateLimit", "Aggregate limit"),
      text("coverageType", "Coverage type"),
      bool("isSelfInsured", "Self-insured"),
      text("lengthOfTimeWithCarrier", "Length of time with carrier"),
      text("carrierPhone", "Carrier phone"),
    ],
    unsupportedDetailKeys: ["carrierAddress"],
  },
  [CredentialType.MalpracticeClaimsHistory]: {
    type: CredentialType.MalpracticeClaimsHistory,
    fields: [
      {
        source: "details",
        key: "noClaims",
        label: "No claims to disclose",
        kind: "boolean",
        required: true,
      },
      { ...date("disclosureDate", "Disclosure date"), required: true },
    ],
    unsupportedDetailKeys: ["claims"],
  },
  [CredentialType.WorkHistory]: {
    type: CredentialType.WorkHistory,
    fields: [
      select({
        key: "kind",
        label: "Kind",
        values: ["employment", "gap"],
        required: true,
      }),
      text("employerName", "Employer"),
      text("position", "Position"),
      { ...text("responsibilities", "Responsibilities"), kind: "textarea" },
      { ...date("startDate", "Start date"), required: true },
      date("endDate", "End date"),
      bool("currentlyEmployed", "Currently employed"),
      text("city", "City"),
      text("state", "State"),
      text("country", "Country"),
      {
        ...text("reasonForDiscontinuance", "Reason for leaving"),
        kind: "textarea",
      },
      { ...text("gapExplanation", "Gap explanation"), kind: "textarea" },
    ],
    unsupportedDetailKeys: ["address", "contact", "ehrs"],
  },
  [CredentialType.HospitalAffiliation]: {
    type: CredentialType.HospitalAffiliation,
    fields: [
      { ...text("hospitalName", "Hospital"), required: true, isIdentity: true },
      select({
        key: "affiliationKind",
        label: "Affiliation kind",
        values: [
          "admitting-privileges",
          "admitting-arrangement",
          "non-admitting",
        ],
        required: true,
      }),
      statusField(CredentialType.HospitalAffiliation, "Privilege status"),
      date("startDate", "Start date"),
      date("endDate", "End date"),
      text("department", "Department"),
      bool("primaryHospital", "Primary hospital"),
      text("admittingPrivilegeType", "Admitting privilege type"),
      num("hospitalPercentage", "Percentage of practice"),
      text("city", "City"),
      text("country", "Country"),
      bool("sameSpecialtyAsAdmitter", "Same specialty as admitter"),
      {
        ...text("arrangementDescription", "Arrangement description"),
        kind: "textarea",
      },
      {
        ...text("reasonsForDiscontinuance", "Reason for discontinuance"),
        kind: "textarea",
      },
      { ...text("description", "Description"), kind: "textarea" },
    ],
    unsupportedDetailKeys: ["address", "contact", "admitter"],
  },
  [CredentialType.CaseLog]: {
    type: CredentialType.CaseLog,
    fields: [
      date("periodStart", "Period start"),
      date("periodEnd", "Period end"),
      text("facility", "Facility"),
      text("specialty", "Specialty"),
      num("procedureCount", "Procedure count"),
    ],
    unsupportedDetailKeys: [],
  },
  [CredentialType.Attestation]: {
    type: CredentialType.Attestation,
    fields: [
      { ...date("signedAt", "Signed on"), required: true },
      select({
        key: "method",
        label: "Method",
        values: ["esign", "wet-signature", "caqh", "assured"],
        required: true,
      }),
      EXPIRES_AT,
      text("questionnaireVersion", "Questionnaire version"),
      text("vendorRef", "Vendor reference"),
    ],
    unsupportedDetailKeys: ["responses"],
  },
  [CredentialType.BackgroundCheck]: {
    type: CredentialType.BackgroundCheck,
    fields: [
      { ...text("checkKind", "Check type"), required: true, isIdentity: true },
      EXPIRES_AT,
      statusField(CredentialType.BackgroundCheck, "Result"),
      text("vendor", "Vendor"),
      text("reportId", "Report ID"),
      text("packageKind", "Package"),
      date("completedAt", "Completed on"),
    ],
    unsupportedDetailKeys: [],
  },
  [CredentialType.PeerReference]: {
    type: CredentialType.PeerReference,
    fields: [
      { ...text("refereeName", "Referee"), required: true, isIdentity: true },
      text("refereeTitle", "Referee title"),
      text("refereeSpecialty", "Referee specialty"),
      text("relationship", "Relationship"),
      num("yearsKnown", "Years known"),
      text("contactEmail", "Email"),
      text("contactPhone", "Phone"),
      date("completedAt", "Completed on"),
      EXPIRES_AT,
    ],
    unsupportedDetailKeys: [],
  },
  [CredentialType.CollaborativeAgreement]: {
    type: CredentialType.CollaborativeAgreement,
    fields: [
      {
        ...text("supervisingPhysicianName", "Supervising physician"),
        required: true,
        isIdentity: true,
      },
      select({
        key: "agreementKind",
        label: "Agreement kind",
        values: ["collaborative", "supervisory", "delegation"],
      }),
      text("supervisingPhysicianNpi", "Supervising physician NPI"),
      text("practiceSetting", "Practice setting"),
      ISSUED_AT,
      EXPIRES_AT,
      statusField(CredentialType.CollaborativeAgreement, "Agreement status"),
    ],
    // A Saile user id, resolved by linking rather than typed into a form.
    unsupportedDetailKeys: ["supervisingPhysicianUserId"],
  },
  [CredentialType.Npi]: {
    type: CredentialType.Npi,
    fields: [
      numberField("NPI"),
      date("enumerationDate", "Enumeration date"),
      list("taxonomyCodes", "Taxonomy codes"),
      text("upin", "UPIN"),
    ],
    unsupportedDetailKeys: [],
  },
  [CredentialType.MedicaidId]: {
    type: CredentialType.MedicaidId,
    fields: [stateField("State", true), numberField("Medicaid ID")],
    unsupportedDetailKeys: [],
  },
  [CredentialType.MedicareId]: {
    type: CredentialType.MedicareId,
    fields: [
      stateField("State", true),
      numberField("Medicare ID / PTAN"),
      select({ key: "kind", label: "Kind", values: ["ptan", "medicare-id"] }),
    ],
    unsupportedDetailKeys: [],
  },
  [CredentialType.PersonalInfo]: {
    type: CredentialType.PersonalInfo,
    fields: [
      date("birthDate", "Date of birth"),
      text("gender", "Gender"),
      text("race", "Race"),
      text("caqhId", "CAQH ID"),
      list("otherNames", "Other names used"),
    ],
    // Personal info is a cluster of structured sub-records (name, addresses,
    // citizenship, military…). Each needs its own sub-form; a flat modal would
    // misrepresent them.
    unsupportedDetailKeys: [
      "name",
      "birthPlace",
      "physical",
      "citizenship",
      "contact",
      "homeAddress",
      "correspondenceAddress",
      "professional",
      "military",
    ],
  },
  [CredentialType.Cv]: {
    type: CredentialType.Cv,
    fields: [ISSUED_AT],
    unsupportedDetailKeys: [],
  },
  [CredentialType.Other]: {
    type: CredentialType.Other,
    fields: [
      /**
       * Every other type puts an issued identifier in `number` — a licence
       * number, a policy number. "Other" has no such thing, so the column
       * carries the only name this document will ever have: what a reader
       * types here is what the record is called, and what a second copy of the
       * same document is matched against.
       *
       * It was labelled "Reference", which named the column rather than the
       * job and left the form asking for a value nobody could source.
       */
      {
        source: "column",
        key: "number",
        label: "Document name",
        kind: "text",
        helpText:
          'What this document is — for example "Employee handbook acknowledgement". Filing another file under the same name adds it to this record instead of starting a new one.',
      },
      ISSUED_AT,
      EXPIRES_AT,
    ],
    unsupportedDetailKeys: [],
    // Stamped by the server from the upload this was filed from, and what an
    // unnamed document is identified by. Editing it by hand would silently
    // re-point the record at a different file.
    serverOwnedDetailKeys: ["fileName"],
  },
};

export function getCredentialFormDescriptor(
  type: CredentialType,
): CredentialFormDescriptor {
  return CREDENTIAL_FORM_DESCRIPTORS[type];
}
